/**
 * Cache Manager
 *
 * Manages cache versioning and invalidation based on backend data updates.
 * Detects when admin changes data and refreshes customer frontend automatically.
 */

const STORAGE_PREFIX = 'axios-cache:';
const VERSION_KEY = 'data-version';

/**
 * Get stored data version for comparison
 */
export function getStoredVersion() {
  try {
    return localStorage.getItem(VERSION_KEY);
  } catch (error) {
    console.warn('Failed to get stored version:', error);
    return null;
  }
}

/**
 * Update stored data version
 */
export function setStoredVersion(version) {
  try {
    localStorage.setItem(VERSION_KEY, version);
  } catch (error) {
    console.warn('Failed to set stored version:', error);
  }
}

/**
 * Check if backend data version has changed
 * Returns true if data needs refresh
 */
export function hasDataChanged(newVersion) {
  if (!newVersion) return false;

  const storedVersion = getStoredVersion();
  if (!storedVersion) {
    setStoredVersion(newVersion);
    return false;
  }

  return newVersion !== storedVersion;
}

/**
 * Clear all cached data
 */
export function clearAllCache() {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(STORAGE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    console.log('Cache cleared');
  } catch (error) {
    console.warn('Failed to clear cache:', error);
  }
}

/**
 * Clear cache for specific endpoints matching pattern
 */
export function clearCachePattern(pattern) {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(STORAGE_PREFIX) && key.includes(pattern)) {
        localStorage.removeItem(key);
      }
    });
    console.log(`Cache cleared for pattern: ${pattern}`);
  } catch (error) {
    console.warn('Failed to clear cache pattern:', error);
  }
}

/**
 * Handle version change from backend
 * Called when X-Data-Version header indicates data changed
 */
export function handleVersionChange(newVersion) {
  if (hasDataChanged(newVersion)) {
    console.log('Backend data updated, refreshing cache...');

    // Clear relevant caches
    clearCachePattern('/items');
    clearCachePattern('/menus');
    clearCachePattern('/categories');

    // Update stored version
    setStoredVersion(newVersion);

    // Notify application (can be used to show "Data updated" message)
    dispatchCacheUpdateEvent();

    return true;
  }

  return false;
}

/**
 * Dispatch custom event when cache is updated
 * Components can listen to this to refresh their data
 */
function dispatchCacheUpdateEvent() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('cache-updated', {
      detail: { timestamp: Date.now() }
    }));
  }
}

/**
 * Hook for components to listen to cache updates
 */
export function useCacheUpdateListener(callback) {
  if (typeof window === 'undefined') return;

  const handler = (event) => callback(event.detail);
  window.addEventListener('cache-updated', handler);

  return () => window.removeEventListener('cache-updated', handler);
}

export default {
  getStoredVersion,
  setStoredVersion,
  hasDataChanged,
  clearAllCache,
  clearCachePattern,
  handleVersionChange,
  useCacheUpdateListener,
};
